import { useState } from 'react'
import cx from 'classnames'
import { isFinite, isNil } from 'lodash'

import { Select as AppSelect, InputNumber } from '@commutifi-fe/ui'
import { FormattedMessage, useIntl } from 'locales/index'
import { DAYS_PER_WEEK } from 'constants/dates'
import styles from './styles.module.scss'

interface IFrequencyValue {
  weeklyFrequency?: number
}

interface IFrequencyOption {
  id: string
  label: React.ReactNode
  max: number
  min: number
  nbOfWeeks: number
}

interface IFrequencySelectorProps {
  className?: string
  onChange?: (value: IFrequencyValue) => void
  value?: IFrequencyValue
  initialValue?: IFrequencyValue
  maxWeeklyFrequency?: number
  minWeeklyFrequency?: number
}

function FrequencySelector({
  className,
  onChange,
  value,
  initialValue,
  maxWeeklyFrequency,
  minWeeklyFrequency
}: IFrequencySelectorProps) {
  const intl = useIntl()
  const frequencyOption: IFrequencyOption = {
    id: 'weekly',
    label: <FormattedMessage id="dayPicker.frequency.weekly" />,
    max: maxWeeklyFrequency || DAYS_PER_WEEK,
    min: isNil(minWeeklyFrequency) ? 1 : minWeeklyFrequency,
    nbOfWeeks: 1
  }

  // Value correspond to antd controlled value injected in the form
  const controlledValue = value

  const weeklyFrequency = controlledValue?.weeklyFrequency
  // Calculate the input value based on the frequecy option. If it is monthly
  // we need to multiple by 4. Otherwise it will multiply by 1
  const defaultInputValue = weeklyFrequency ? weeklyFrequency * frequencyOption.nbOfWeeks : ''

  const [isInputNumberOnFocus, setIsInputNumberOnFocus] = useState(false)
  const [inputValue, setInputValue] = useState<number | undefined | ''>(
    initialValue?.weeklyFrequency || defaultInputValue
  )

  const callOnChange = (value: number | '') => {
    // If the current frequency option is monthly we divide by the number of week in a month
    const weeklyFrequency = value !== '' ? value : undefined
    onChange && onChange({ weeklyFrequency })
  }

  const handleInputNumberFocus = () => {
    setIsInputNumberOnFocus(true)
  }
  const handleInputNumberBlur = () => {
    setIsInputNumberOnFocus(false)
  }
  const handleInputChange = (value: number) => {
    setInputValue(isFinite(value) && value >= 0 ? value : undefined)
    // Call on change with the current frequency selected and the new value
    callOnChange(value)
  }

  const inputFormatter = (value: any): string => {
    // NOTE that when we change the max dynamically on the number input
    // it resets the value to null. In that case we take the previous value
    // and check if it is valid. If not we reset it to 1 day
    const currentValue = value ? parseInt(value) : inputValue
    const isPositiveFiniteValue = () => currentValue && isFinite(currentValue) && currentValue >= 0
    const formattedValue = isPositiveFiniteValue()
      ? intl.formatMessage({ id: 'dayPicker.frequency.count' }, { days: currentValue })
      : ''

    return isInputNumberOnFocus ? (isPositiveFiniteValue() ? `${currentValue}` : '') : formattedValue
  }
  const inputParser = (input: string) => {
    const value = input.replace(/\D/g, '')
    return parseInt(value) >= 0 ? parseInt(value) : -1
  }

  return (
    <div className={cx(styles.container, className)}>
      <div className={styles.item}>
        <InputNumber
          aria-label="Enter number of days"
          placeholder={intl.formatMessage({ id: 'onboarding.numberInput.placeholder' })}
          value={inputValue === '' ? undefined : inputValue}
          formatter={inputFormatter}
          parser={inputParser}
          onChange={handleInputChange}
          onFocus={handleInputNumberFocus}
          onBlur={handleInputNumberBlur}
          max={frequencyOption.max}
          min={frequencyOption.min}
          style={{ width: '100%' }}
        />
      </div>
      <div className={styles.item}>
        <AppSelect
          defaultValue={frequencyOption.id}
          disabled
          options={[
            {
              id: frequencyOption.id,
              value: frequencyOption.id,
              label: frequencyOption.label
            }
          ]}
        />
      </div>
    </div>
  )
}

export default FrequencySelector
